import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Clock, User, GraduationCap, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import {
  getPunctualityVerdict,
  getTutorLatenessSeconds,
  getStudentLatenessSeconds,
  formatLateness,
  DEFAULT_THRESHOLDS,
} from '../lib/punctuality';

const ITEMS_PER_PAGE = 15;

const timeRangeOptions = [
  { label: '7d', value: 7 },
  { label: '28d', value: 28 },
  { label: '90d', value: 90 },
  { label: '1y', value: 365 },
  { label: 'All', value: null },
];

const Punctuality = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [tutorFilter, setTutorFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [orgSettings, setOrgSettings] = useState(null);
  const [timeRange, setTimeRange] = useState(90);

  useEffect(() => {
    if (!organizationId) return;
    loadLateness();
  }, [organizationId, timeRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, severityFilter, tutorFilter]);

  const loadLateness = async () => {
    setLoading(true);
    try {
      try {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', organizationId)
          .single();

        if (orgData) {
          setOrgSettings({
            minor: orgData.lateness_minor_minutes ?? 3,
            moderate: orgData.lateness_moderate_minutes ?? 6,
            severe: orgData.lateness_severe_minutes ?? 10,
          });
        }
      } catch (e) {
        console.warn('Could not load org thresholds, using defaults:', e);
      }

      let query = supabase
        .from('sessions')
        .select('id, session_id, tutor_name, student_name, lesson_timestamp, duration_minutes, tutor_join_lateness_seconds, lateness_seconds')
        .eq('organization_id', organizationId)
        .order('lesson_timestamp', { ascending: false });

      if (timeRange) {
        const d = new Date();
        d.setDate(d.getDate() - timeRange);
        query = query.gte('lesson_timestamp', d.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      const lateSessions = (data || []).filter(s => {
        if (s.tutor_join_lateness_seconds && s.tutor_join_lateness_seconds > 0) return true;
        if (s.lateness_seconds && s.lateness_seconds !== 0) return true;
        return false;
      });

      setSessions(lateSessions);
    } catch (err) {
      console.error('Error loading lateness:', err);
    }
    setLoading(false);
  };

  const getLatenessInfo = (s) => {
    const v = getPunctualityVerdict(s, orgSettings || DEFAULT_THRESHOLDS);
    return {
      whoWasLate: v.who,
      minutes: v.minutes,
      seconds: v.seconds,
      severity: v.severity || 'minor',
    };
  };

  const getTutorLateness = getTutorLatenessSeconds;
  const getStudentLateness = getStudentLatenessSeconds;

  const tutors = useMemo(() => {
    const unique = [...new Set(sessions.map(s => s.tutor_name).filter(Boolean))];
    return unique.sort();
  }, [sessions]);

  const filtered = useMemo(() => {
    const minorThreshold = orgSettings?.minor ?? 3;
    return sessions.filter(s => {
      const info = getLatenessInfo(s);
      if (info.minutes < minorThreshold) return false;
      if (filter === 'tutor' && info.whoWasLate !== 'tutor') return false;
      if (filter === 'student' && info.whoWasLate !== 'student') return false;
      if (severityFilter !== 'all' && info.severity !== severityFilter) return false;
      if (tutorFilter && s.tutor_name !== tutorFilter) return false;
      return true;
    });
  }, [sessions, filter, severityFilter, tutorFilter, orgSettings]);

  const stats = useMemo(() => {
    const minorThreshold = orgSettings?.minor ?? 3;
    const aboveThreshold = sessions.filter(s => getLatenessInfo(s).minutes >= minorThreshold);
    const tutorLate = aboveThreshold.filter(s => getLatenessInfo(s).whoWasLate === 'tutor').length;
    const studentLate = aboveThreshold.filter(s => getLatenessInfo(s).whoWasLate === 'student').length;
    return { total: aboveThreshold.length, tutorLate, studentLate };
  }, [sessions, orgSettings]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Punctuality
        </h1>
        <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
          {timeRangeOptions.map(opt => (
            <button
              key={opt.label}
              onClick={() => { setTimeRange(opt.value); setCurrentPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                timeRange === opt.value
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Late Sessions', count: stats.total, filterKey: 'all' },
          { label: 'Tutor Late', count: stats.tutorLate, filterKey: 'tutor', icon: User },
          { label: 'Student Late', count: stats.studentLate, filterKey: 'student', icon: GraduationCap },
        ].map(card => (
          <button
            key={card.filterKey}
            onClick={() => setFilter(card.filterKey)}
            className={`${cardClass} p-5 text-left transition-colors ${
              filter === card.filterKey ? isDark ? 'ring-1 ring-[#7662ac]' : 'ring-1 ring-[#586f8d]' : ''
            }`}
          >
            <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{card.label}</div>
            <div className="flex items-center gap-2 mt-1">
              {card.icon && <card.icon className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />}
              <span className={`text-2xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{card.count}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className={`text-sm border rounded-lg px-3 py-1.5 ${isDark ? 'bg-[#18181b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
        >
          <option value="all">All Severities</option>
          <option value="severe">Severe ({orgSettings?.severe ?? 10}+ min)</option>
          <option value="moderate">Moderate ({orgSettings?.moderate ?? 6}+ min)</option>
          <option value="minor">Minor ({orgSettings?.minor ?? 3}+ min)</option>
        </select>
        <select
          value={tutorFilter}
          onChange={e => setTutorFilter(e.target.value)}
          className={`text-sm border rounded-lg px-3 py-1.5 ${isDark ? 'bg-[#18181b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
        >
          <option value="">All Tutors</option>
          {tutors.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className={`text-xs ml-auto ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          {filtered.length} session{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Sessions List */}
      <div className={cardClass}>
        {loading ? (
          <div className="space-y-3 p-4 animate-pulse">
            {[1,2,3,4,5].map(i => (
              <div key={i} className={`h-14 rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            <Clock className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">{sessions.length > 0 ? 'No lateness records match your filters' : 'No lateness records found'}</p>
          </div>
        ) : (
          <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
            {paginated.map(s => {
              const info = getLatenessInfo(s);
              const tutorLate = getTutorLateness(s);
              const studentLate = getStudentLateness(s);
              const isExpanded = expandedId === s.id;

              return (
                <div key={s.id}>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : s.id)}
                    className={`w-full px-5 py-4 text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
                          {info.whoWasLate === 'tutor'
                            ? <User className={`h-3.5 w-3.5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
                            : <GraduationCap className={`h-3.5 w-3.5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor_name}</span>
                            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>→</span>
                            <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.student_name}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-xs font-medium ${
                              info.severity === 'severe' ? 'text-amber-400'
                              : info.severity === 'moderate' ? isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'
                              : isDark ? 'text-[#a1a1aa]' : 'text-[#666]'
                            }`}>
                              {info.whoWasLate === 'tutor' ? 'Tutor' : 'Student'} late · {formatLateness(info.seconds)}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              info.severity === 'severe'
                                ? isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                                : info.severity === 'moderate'
                                ? isDark ? 'bg-[#27272a] text-[#7662ac]' : 'bg-[#f5f0eb] text-[#586f8d]'
                                : isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
                            }`}>
                              {info.severity}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                          {new Date(s.lesson_timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {isExpanded
                          ? <ChevronUp className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                          : <ChevronDown className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                        }
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className={`px-5 pb-4 ${isDark ? 'bg-[#0d0d0f]' : 'bg-[#fafafa]'}`}>
                      <div className="flex gap-6 text-sm mt-1">
                        <div className="flex items-center gap-2">
                          <User className={`h-3.5 w-3.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor_name}</span>
                          {tutorLate > 0 ? (
                            <span className="text-amber-400">{formatLateness(tutorLate)} late</span>
                          ) : (
                            <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>on time</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <GraduationCap className={`h-3.5 w-3.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.student_name}</span>
                          {studentLate > 0 ? (
                            <span className="text-amber-400">{formatLateness(studentLate)} late</span>
                          ) : (
                            <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>on time</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/lessons/${s.id}`)}
                        className={`mt-3 text-xs ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                      >
                        View session →
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Info Note */}
      <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
        Lateness uses join times vs an inferred scheduled time (nearest half hour). Only flagged when
        the first person joins 10+ min after that half hour, to reduce false positives.
        {orgSettings && ` Thresholds: Minor (${orgSettings.minor}+ min), Moderate (${orgSettings.moderate}+ min), Severe (${orgSettings.severe}+ min).`}
      </div>
    </div>
  );
};

export default Punctuality;
